import { parseAsString, useQueryState } from "nuqs";
import {
	type ChangeEvent,
	type ReactElement,
	useEffect,
	useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useMatches, useNavigate } from "react-router";
import { SearchBarBaseComponent } from "~/components/search-bar.component";
import { hasPageNameHandle } from "~/routes/helpers/get-page-name-handle";

interface SearchBarProps {
	leftIcon?: ReactElement;
	autoFocus?: boolean;
}

// Both product-listing routes (global `/products` and the vendor-scoped
// `/:vendorName/:vendorId/:deliveryMethod`) export this page name via their
// route handle. On these routes the `?search=` param is consumed in place,
// so the SearchBar must not navigate away (MKP-2035: navigating dropped the
// vendor context and silently reset vendorId to `top`).
const PRODUCT_LISTING_PAGE_NAME = "products";

/**
 * Two-layer state:
 *   - `localSearch` is the urgent, controlled value driving the <input>.
 *     Typing updates this synchronously so there is never a keystroke lag
 *     even when the surrounding product list does expensive re-renders.
 *   - `search` is the deferred URL query state via `nuqs`. Syncing it is
 *     wrapped in `startTransition` so the URL update + downstream product-
 *     list re-render doesn't block user input. This matches the reviewer
 *     guidance to "keep text-input state urgent and only transition
 *     downstream expensive work".
 *
 * A one-way effect re-syncs `localSearch` from `search` whenever the URL
 * changes externally (back-navigation, link click with `?search=` query
 * param, etc.) so the displayed value never drifts from the canonical URL.
 */
export function SearchBar({ leftIcon, autoFocus }: SearchBarProps) {
	const navigate = useNavigate();
	const { t } = useTranslation("product");
	const location = useLocation();
	const matches = useMatches();
	const [search, setSearch] = useQueryState(
		"search",
		parseAsString.withDefault(""),
	);
	const [localSearch, setLocalSearch] = useState(search);
	const currentRoute = matches.at(-1);
	const isProductListingRoute =
		hasPageNameHandle(currentRoute?.handle) &&
		currentRoute.handle.pageName === PRODUCT_LISTING_PAGE_NAME;

	// When a search term exists on a non-listing route (PDP, favorites,
	// profile…), move to `/products` carrying the current query params so
	// the vendor context (vendorId, deliveryMethod, postalCode, filters)
	// survives the navigation.
	useEffect(() => {
		if (!search || isProductListingRoute) return;
		const params = new URLSearchParams(location.search);
		params.set("search", search);
		navigate(`/products?${params.toString()}`);
	}, [search, isProductListingRoute, location.search, navigate]);

	// Re-sync local value when the URL changes externally (e.g. back button).
	useEffect(() => {
		setLocalSearch(search);
	}, [search]);

	const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
		const value = event.currentTarget.value;
		// Urgent: keystroke feedback must never be deferred.
		setLocalSearch(value);
		// nuqs handles its own throttling for URL updates; wrapping this in
		// React's startTransition previously caused the URL to lag behind the
		// input long enough for E2E tests (and downstream !search checks in
		// product-list) to race.
		setSearch(value);
	};

	const handleClear = () => {
		setLocalSearch("");
		setSearch(null);
	};

	return (
		<SearchBarBaseComponent
			leftIcon={leftIcon}
			autoFocus={autoFocus}
			onChange={handleChange}
			placeholder={t("search.placeholder")}
			search={localSearch}
			onRightIconClick={handleClear}
		/>
	);
}
